import React, { useEffect, useState } from "react";
import { useTitle } from "../../context/TitleContext";
import { useDispatch, useSelector } from "react-redux";
import { fetchSettings, updateSettings, clearState } from "../../redux/Slices/SettingsSlice";
import { toast } from "react-toastify";
import { validateFieldLanguage } from "../../Utils/languageValidation";

import { useTranslation } from "react-i18next";

export default function AboutUsForm() {
  const { i18n } = useTranslation("global");
  const { setTitle } = useTitle();
  const dispatch = useDispatch();

  const {
    settings,
    aboutImage,
    isLoading,
    updateLoading,
    error,
    success
  } = useSelector((state) => state.settings);

  const [showEditForm, setShowEditForm] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState({
    about_ar: "",
    about_en: "",
    who_ar: "",
    who_en: "",
    vision_ar: "",
    vision_en: "",
    feature_text_ar: "",
    feature_text_en: "",
    address_ar: "",
    address_en: "",
    phone: "",
    email: "",
    working_hours: "",
    students: "",
    books_count: "",
    support: "",
    success_rate: "",
    facebook_link: "",
    whats_link: "",
    telegram_link: "",
    instgram_link: "",
    tiktok_link: "",
    footer_text_ar: "",
    footer_text_en: "",
    slider_text_ar: "",
    slider_text_en: "",
    slider_title_ar: "",
    slider_title_en: "",
    // BUG-54 fix: القيمة الافتراضية يجب أن تكون رقماً وليست نص التسمية
    shipping_extra_per_item: 0
  });

  useEffect(() => {
    setTitle('من نحن');
    dispatch(fetchSettings());
    dispatch(clearState());
  }, [setTitle, dispatch]);

  // تحديث البيانات عند تحميل الإعدادات
  useEffect(() => {
    if (settings) {
      const newFormData = {};
      settings.forEach(setting => {
        newFormData[setting.key] = setting.value;
      });
      // BUG-54 fix: التأكد أن قيمة سعر المنتج الثاني في الشحن رقم وليست نصاً (مثل نص التسمية)
      if (
        newFormData.shipping_extra_per_item === undefined ||
        newFormData.shipping_extra_per_item === null ||
        newFormData.shipping_extra_per_item === "" ||
        isNaN(Number(newFormData.shipping_extra_per_item))
      ) {
        newFormData.shipping_extra_per_item = 0;
      }
      setOriginalData(newFormData);
      setFormData(prev => ({ ...prev, ...newFormData }));
    }
    if (aboutImage) {
      setImagePreview(aboutImage);
    }
  }, [settings, aboutImage]);

  // عرض رسائل الخطأ
  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-center",
        zIndex: 9999,
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      dispatch(clearState());
    }
  }, [error, dispatch]);

  // التحقق من التغييرات
  useEffect(() => {
    if (originalData) {
      const hasFormChanges =
        formData.about_ar !== originalData.about_ar ||
        formData.about_en !== originalData.about_en ||
        formData.who_ar !== originalData.who_ar ||
        formData.who_en !== originalData.who_en ||
        formData.vision_ar !== originalData.vision_ar ||
        formData.vision_en !== originalData.vision_en ||
        formData.feature_text_ar !== originalData.feature_text_ar ||
        formData.feature_text_en !== originalData.feature_text_en ||
        formData.address_ar !== originalData.address_ar ||
        formData.address_en !== originalData.address_en ||
        formData.phone !== originalData.phone ||
        formData.email !== originalData.email ||
        formData.working_hours !== originalData.working_hours ||
        formData.students !== originalData.students ||
        formData.books_count !== originalData.books_count ||
        formData.support !== originalData.support ||
        formData.success_rate !== originalData.success_rate ||
        formData.facebook_link !== originalData.facebook_link ||
        formData.whats_link !== originalData.whats_link ||
        formData.telegram_link !== originalData.telegram_link ||
        formData.instgram_link !== originalData.instgram_link ||
        formData.tiktok_link !== originalData.tiktok_link ||
        formData.footer_text_ar !== originalData.footer_text_ar ||
        formData.footer_text_en !== originalData.footer_text_en ||
        formData.slider_text_ar !== originalData.slider_text_ar ||
        formData.slider_text_en !== originalData.slider_text_en ||
        formData.slider_title_ar !== originalData.slider_title_ar ||
        formData.slider_title_en !== originalData.slider_title_en ||
        formData.shipping_extra_per_item !== originalData.shipping_extra_per_item ||
        imageFile !== null; // إذا تم اختيار صورة جديدة

      setHasChanges(hasFormChanges);
    }
  }, [formData, originalData, imageFile]);

  // عرض رسائل النجاح
  useEffect(() => {
    if (success) {
      toast.success(success, {
        position: "top-center",
        zIndex: 9999,
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });

      if (success.includes('تم تحديث الإعدادات')) {
        setShowEditForm(false);
        setImageFile(null);
        setImagePreview(aboutImage); // إعادة تعيين preview للصورة الحالية
        dispatch(fetchSettings()); // إعادة تحميل البيانات
      }

      dispatch(clearState());
    }
  }, [success, aboutImage, dispatch]);

  // Get alignment class based on current language
  const getAlignmentClass = () => {
    return i18n.language === 'ar' ? 'text-right' : 'text-left';
  };

  // دالة للحصول على قيمة الإعداد
  const getSettingValue = (key) => {
    if (!settings) return '';
    const setting = settings.find(s => s.key === key);
    return setting ? setting.value : '';
  };

  // BUG-54 fix: قيمة رقمية آمنة لعرض سعر المنتج الثاني في الشحن (تفادي عرض نص بدلاً من رقم)
  const getNumericSettingValue = (key) => {
    const raw = getSettingValue(key);
    const num = Number(raw);
    return raw !== '' && !isNaN(num) ? raw : 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let cleanValue = value;

    // منع الأرقام السالبة للحقول الرقمية والإحصائية
    if (["students", "shipping_extra_per_item", "books_count", "success_rate"].includes(name)) {
      cleanValue = value.replace(/-/g, "");
      const numValue = parseFloat(cleanValue);
      if (numValue < 0) {
        return; // منع القيم السالبة
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: cleanValue
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // إنشاء preview للصورة الجديدة
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // BUG-55 fix: التحقق من أن رقم الهاتف يحتوي على أرقام فقط
    if (formData.phone && !/^\+?\d{7,15}$/.test(String(formData.phone).trim())) {
      return toast.error("رقم الهاتف يجب أن يحتوي على أرقام فقط");
    }

    // BUG-55 fix: التحقق من أن عدد الطلاب رقم صحيح موجب
    if (
      formData.students !== "" &&
      formData.students !== null &&
      formData.students !== undefined &&
      (isNaN(Number(formData.students)) || Number(formData.students) < 0)
    ) {
      return toast.error("عدد الطلاب يجب أن يكون رقماً صحيحاً");
    }

    // BUG-54 fix: التحقق من أن سعر المنتج الثاني في الشحن رقم صحيح موجب
    if (isNaN(Number(formData.shipping_extra_per_item)) || Number(formData.shipping_extra_per_item) < 0) {
      return toast.error("سعر المنتج الثاني في الشحن يجب أن يكون رقماً صحيحاً");
    }

    // التحقق من عدم وجود قيم سالبة للكتب ونسبة النجاح
    if (formData.books_count && (isNaN(parseFloat(formData.books_count)) ? String(formData.books_count).includes("-") : parseFloat(formData.books_count) < 0)) {
      return toast.error("عدد الكتب لا يمكن أن يكون سالباً");
    }
    if (formData.success_rate && (isNaN(parseFloat(formData.success_rate)) ? String(formData.success_rate).includes("-") : parseFloat(formData.success_rate) < 0)) {
      return toast.error("نسبة النجاح لا يمكن أن تكون سالبة");
    }

    // التحقق من لغة الحقول (منع خلط العربي بالإنجليزي)
    const langFields = [
      { key: "about_ar", lang: "ar", label: "من نحن بالعربية" },
      { key: "about_en", lang: "en", label: "About Us in English" },
      { key: "who_ar", lang: "ar", label: "من نحن (العنوان) بالعربية" },
      { key: "who_en", lang: "en", label: "Who We Are in English" },
      { key: "vision_ar", lang: "ar", label: "رؤيتنا بالعربية" },
      { key: "vision_en", lang: "en", label: "Vision in English" },
      { key: "feature_text_ar", lang: "ar", label: "النص المميز بالعربية" },
      { key: "feature_text_en", lang: "en", label: "Featured Text in English" },
      { key: "address_ar", lang: "ar", label: "العنوان بالعربية" },
      { key: "address_en", lang: "en", label: "Address in English" },
      { key: "footer_text_ar", lang: "ar", label: "نص الفوتر بالعربية" },
      { key: "footer_text_en", lang: "en", label: "Footer Text in English" },
      { key: "slider_text_ar", lang: "ar", label: "نص السلايدر بالعربية" },
      { key: "slider_text_en", lang: "en", label: "Slider Text in English" },
      { key: "slider_title_ar", lang: "ar", label: "عنوان السلايدر بالعربية" },
      { key: "slider_title_en", lang: "en", label: "Slider Title in English" },
    ];

    for (const field of langFields) {
      const val = formData[field.key];
      const errorMsg = validateFieldLanguage(val, field.lang);
      if (errorMsg) {
        return toast.error(`${field.label}: ${errorMsg}`);
      }
    }

    // إنشاء FormData وإضافة البيانات
    const submitData = new FormData();

    // إضافة جميع البيانات النصية
    Object.entries(formData).forEach(([key, value]) => {
      submitData.append(key, value);
      // console.log(`Adding to FormData: ${key} = ${value}`);
    });

    // إضافة الصورة إذا تم اختيارها
    if (imageFile) {
      submitData.append('about_image', imageFile);
      // console.log('Adding image file:', imageFile.name);
    }



    // console.log('Dispatching updateSettings...');
    dispatch(updateSettings(submitData));
  };

  const handleEditClick = () => {
    setShowEditForm(true);
    // إعادة تعيين الصورة
    setImageFile(null);
    setImagePreview(aboutImage);
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setImageFile(null);
    setImagePreview(aboutImage);
    // إعادة تحميل البيانات الأصلية
    if (settings) {
      const newFormData = {};
      settings.forEach(setting => {
        newFormData[setting.key] = setting.value;
      });
      setFormData(prev => ({ ...prev, ...newFormData }));
    }
  };

  if (isLoading) {
    return (
      <div className="container mt-5">
        <div className="card shadow p-4">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">جار التحميل...</p>
          </div>
        </div>
      </div>
    );
  }

  // إضافة رسالة إذا لم توجد إعدادات
  if (!settings || settings.length === 0) {
    return (
      <div className="container mt-5">
        <div className="card shadow p-4">
          <div className="text-center">
            <h4 className="text-muted">لا توجد إعدادات متاحة</h4>
            <p className="text-muted">تأكد من اتصال الإنترنت أو حاول تحديث الصفحة</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                dispatch(fetchSettings());
              }}
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="card shadow p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className={`mb-0 ${getAlignmentClass()}`}>إعدادات من نحن</h4>
          {!showEditForm && (
            <button
              className="btn btn-danger"
              onClick={handleEditClick}
            >
              <i className="bi bi-pencil-square mx-2"></i>
              تعديل الإعدادات
            </button>
          )}
        </div>

        {/* عرض البيانات الحالية */}
        {!showEditForm && (
          <div className="row g-4">
            {/* BUG-52 fix: تمت إزالة قسم "المعلومات الأساسية" المكرر (كان يعرض نفس حقول "من نحن" مرتين) */}
            {/* قسم من نحن */}
            <div className="col-12">
              <div className="card border-light">
                <div className="card-header bg-light">
                  <h5 className={`mb-0 ${getAlignmentClass()}`}>من نحن</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>من نحن (العربية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('who_ar')}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>من نحن (الإنجليزية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('who_en')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* قسم الرؤية */}
            <div className="col-12">
              <div className="card border-light">
                <div className="card-header bg-light">
                  <h5 className={`mb-0 ${getAlignmentClass()}`}>الرؤية</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>الرؤية (العربية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('vision_ar')}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>الرؤية (الإنجليزية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('vision_en')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* قسم النص المميز */}
            <div className="col-12">
              <div className="card border-light">
                <div className="card-header bg-light">
                  <h5 className={`mb-0 ${getAlignmentClass()}`}>النص المميز</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>النص المميز (العربية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('feature_text_ar')}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>النص المميز (الإنجليزية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('feature_text_en')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* قسم الإحصائيات */}
            <div className="col-12">
              <div className="card border-light">
                <div className="card-header bg-light">
                  <h5 className={`mb-0 ${getAlignmentClass()}`}>الإحصائيات</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>عدد الطلاب</label>
                      <div className="p-3 bg-light rounded text-center">
                        <h4 className="mb-0 text-primary">{getSettingValue('students')}</h4>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>عدد الكتب</label>
                      <div className="p-3 bg-light rounded text-center">
                        <h4 className="mb-0 text-primary">{getSettingValue('books_count')}</h4>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>الدعم</label>
                      <div className="p-3 bg-light rounded text-center">
                        <h4 className="mb-0 text-primary">{getSettingValue('support')}</h4>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>معدل النجاح</label>
                      <div className="p-3 bg-light rounded text-center">
                        <h4 className="mb-0 text-primary">{getSettingValue('success_rate')}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* قسم معلومات الاتصال */}
            <div className="col-12">
              <div className="card border-light">
                <div className="card-header bg-light">
                  <h5 className={`mb-0 ${getAlignmentClass()}`}>معلومات الاتصال</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>العنوان (العربية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('address_ar')}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>العنوان (الإنجليزية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('address_en')}</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>رقم الهاتف</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('phone')}</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>البريد الإلكتروني</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('email')}</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>ساعات العمل</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('working_hours')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* قسم روابط التواصل الاجتماعي */}
            <div className="col-12">
              <div className="card border-light">
                <div className="card-header bg-light">
                  <h5 className={`mb-0 ${getAlignmentClass()}`}>روابط التواصل الاجتماعي</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>رابط فيسبوك</label>
                      <div className="p-3 bg-light rounded">
                        <a href={getSettingValue('facebook_link')} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                          {getSettingValue('facebook_link')}
                        </a>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>رابط واتساب</label>
                      <div className="p-3 bg-light rounded">
                        <a href={getSettingValue('whats_link')} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                          {getSettingValue('whats_link')}
                        </a>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>رابط تليجرام</label>
                      <div className="p-3 bg-light rounded">
                        <a href={getSettingValue('telegram_link')} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                          {getSettingValue('telegram_link')}
                        </a>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>رابط انستجرام</label>
                      <div className="p-3 bg-light rounded">
                        <a href={getSettingValue('instgram_link')} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                          {getSettingValue('instgram_link')}
                        </a>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>رابط تيك توك</label>
                      <div className="p-3 bg-light rounded">
                        <a href={getSettingValue('tiktok_link')} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                          {getSettingValue('tiktok_link')}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* قسم نصوص إضافية */}
            <div className="col-12">
              <div className="card border-light">
                <div className="card-header bg-light">
                  <h5 className={`mb-0 ${getAlignmentClass()}`}>نصوص إضافية</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>نص الفوتر (العربية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('footer_text_ar')}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>نص الفوتر (الإنجليزية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('footer_text_en')}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>نص السلايدر (العربية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('slider_text_ar')}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>نص السلايدر (الإنجليزية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('slider_text_en')}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>عنوان السلايدر (العربية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('slider_title_ar')}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className={`form-label fw-bold ${getAlignmentClass()}`}>عنوان السلايدر (الإنجليزية)</label>
                      <div className="p-3 bg-light rounded">
                        <p className={`mb-0 ${getAlignmentClass()}`}>{getSettingValue('slider_title_en')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="card border-light">
                <div className="card-header bg-light">
                  <h5 className={`mb-0 ${getAlignmentClass()}`}>سعر المنتج الثاني في الشحن</h5>
                </div>
                <div className="card-body">
                  <div className="p-3 bg-light rounded">
                    <p className={`mb-0 ${getAlignmentClass()}`}>{getNumericSettingValue('shipping_extra_per_item')}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="card border-light">
                <div className="card-header bg-light">
                  <h5 className={`mb-0 ${getAlignmentClass()}`}> صورة من نحن </h5>
                </div>
                <div className="card-body">
                  {aboutImage && (
                    <div className="text-center">
                      <img
                        src={aboutImage}
                        alt="about preview"
                        style={{ maxWidth: "400px", borderRadius: "8px" }}
                        className="img-fluid"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* فورم التعديل */}
        {showEditForm && (
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className={`mb-0 ${getAlignmentClass()}`}>تعديل الإعدادات</h5>
              <button
                className="btn btn-secondary"
                onClick={handleCancelEdit}
              >
                <i className="bi bi-x-circle mx-2"></i>
                إلغاء
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* قسم صورة من نحن */}
              <div className="mb-4">
                <h6 className={`mb-3 ${getAlignmentClass()}`}>صورة من نحن</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>اختر صورة جديدة</label>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <small className="text-muted">الصيغ المدعومة: JPG, PNG, GIF</small>
                  </div>
                  <div className="col-md-6">
                    {imagePreview && (
                      <div>
                        <label className={`form-label ${getAlignmentClass()}`}>معاينة الصورة</label>
                        <div className="text-center">
                          <img
                            src={imagePreview}
                            alt="preview"
                            style={{ maxWidth: "200px", borderRadius: "8px" }}
                            className="img-fluid border"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* BUG-52 fix: تمت إزالة قسم "المعلومات الأساسية" المكرر (كان يعرض نفس حقول "من نحن" مرتين) */}
              {/* قسم من نحن */}
              <div className="mb-4">
                <h6 className={`mb-3 ${getAlignmentClass()}`}>من نحن</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>من نحن (العربية)</label>
                    <textarea
                      className="form-control"
                      name="who_ar"
                      value={formData.who_ar}
                      onChange={handleChange}
                      rows="3"
                      placeholder="أدخل وصف من نحن باللغة العربية"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>من نحن (الإنجليزية)</label>
                    <textarea
                      className="form-control"
                      name="who_en"
                      value={formData.who_en}
                      onChange={handleChange}
                      rows="3"
                      placeholder="أدخل وصف من نحن باللغة الإنجليزية"
                    />
                  </div>
                </div>
              </div>

              {/* قسم الرؤية */}
              <div className="mb-4">
                <h6 className={`mb-3 ${getAlignmentClass()}`}>الرؤية</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>الرؤية (العربية)</label>
                    <textarea
                      className="form-control"
                      name="vision_ar"
                      value={formData.vision_ar}
                      onChange={handleChange}
                      rows="3"
                      placeholder="أدخل الرؤية باللغة العربية"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>الرؤية (الإنجليزية)</label>
                    <textarea
                      className="form-control"
                      name="vision_en"
                      value={formData.vision_en}
                      onChange={handleChange}
                      rows="3"
                      placeholder="أدخل الرؤية باللغة الإنجليزية"
                    />
                  </div>
                </div>
              </div>

              {/* قسم النص المميز */}
              <div className="mb-4">
                <h6 className={`mb-3 ${getAlignmentClass()}`}>النص المميز</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>النص المميز (العربية)</label>
                    <textarea
                      className="form-control"
                      name="feature_text_ar"
                      value={formData.feature_text_ar}
                      onChange={handleChange}
                      rows="4"
                      placeholder="أدخل النص المميز باللغة العربية"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>النص المميز (الإنجليزية)</label>
                    <textarea
                      className="form-control"
                      name="feature_text_en"
                      value={formData.feature_text_en}
                      onChange={handleChange}
                      rows="4"
                      placeholder="أدخل النص المميز باللغة الإنجليزية"
                    />
                  </div>
                </div>
              </div>

              {/* قسم الإحصائيات */}
              <div className="mb-4">
                <h6 className={`mb-3 ${getAlignmentClass()}`}>الإحصائيات</h6>
                <div className="row g-3">
                  <div className="col-md-3">
                    <label className={`form-label ${getAlignmentClass()}`}>عدد الطلاب</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      name="students"
                      value={formData.students}
                      onChange={handleChange}
                      placeholder="مثال: 100000"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className={`form-label ${getAlignmentClass()}`}>عدد الكتب</label>
                    <input
                      type="text"
                      className="form-control"
                      name="books_count"
                      value={formData.books_count}
                      onChange={handleChange}
                      placeholder="مثال: 3500+"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className={`form-label ${getAlignmentClass()}`}>الدعم</label>
                    <input
                      type="text"
                      className="form-control"
                      name="support"
                      value={formData.support}
                      onChange={handleChange}
                      placeholder="مثال: 24/7"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className={`form-label ${getAlignmentClass()}`}>معدل النجاح</label>
                    <input
                      type="text"
                      className="form-control"
                      name="success_rate"
                      value={formData.success_rate}
                      onChange={handleChange}
                      placeholder="مثال: 98%"
                    />
                  </div>
                </div>
              </div>

              {/* قسم معلومات الاتصال */}
              <div className="mb-4">
                <h6 className={`mb-3 ${getAlignmentClass()}`}>معلومات الاتصال</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>العنوان (العربية)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address_ar"
                      value={formData.address_ar}
                      onChange={handleChange}
                      placeholder="أدخل العنوان باللغة العربية"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>العنوان (الإنجليزية)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="address_en"
                      value={formData.address_en}
                      onChange={handleChange}
                      placeholder="أدخل العنوان باللغة الإنجليزية"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className={`form-label ${getAlignmentClass()}`}>رقم الهاتف</label>
                    <input
                      type="text"
                      className="form-control"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="أدخل رقم الهاتف"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className={`form-label ${getAlignmentClass()}`}>البريد الإلكتروني</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="أدخل البريد الإلكتروني"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className={`form-label ${getAlignmentClass()}`}>ساعات العمل</label>
                    <input
                      type="text"
                      className="form-control"
                      name="working_hours"
                      value={formData.working_hours}
                      onChange={handleChange}
                      placeholder="مثال: 24 ساعة"
                    />
                  </div>
                </div>
              </div>

              {/* قسم روابط التواصل الاجتماعي */}
              <div className="mb-4">
                <h6 className={`mb-3 ${getAlignmentClass()}`}>روابط التواصل الاجتماعي</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>رابط فيسبوك</label>
                    <input
                      type="url"
                      className="form-control"
                      name="facebook_link"
                      value={formData.facebook_link}
                      onChange={handleChange}
                      placeholder="https://facebook.com/yourpage"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>رابط واتساب</label>
                    <input
                      type="url"
                      className="form-control"
                      name="whats_link"
                      value={formData.whats_link}
                      onChange={handleChange}
                      placeholder="https://wa.me/201234567890"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>رابط تليجرام</label>
                    <input
                      type="url"
                      className="form-control"
                      name="telegram_link"
                      value={formData.telegram_link}
                      onChange={handleChange}
                      placeholder="https://telegram.com/yourpage"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>رابط انستجرام</label>
                    <input
                      type="url"
                      className="form-control"
                      name="instgram_link"
                      value={formData.instgram_link}
                      onChange={handleChange}
                      placeholder="https://instagram.com/yourpage"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>رابط تيك توك</label>
                    <input
                      type="url"
                      className="form-control"
                      name="tiktok_link"
                      value={formData.tiktok_link}
                      onChange={handleChange}
                      placeholder="https://tiktok.com/@yourpage"
                    />
                  </div>
                </div>
              </div>

              {/* قسم نصوص إضافية */}
              <div className="mb-4">
                <h6 className={`mb-3 ${getAlignmentClass()}`}>نصوص إضافية</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>نص الفوتر (العربية)</label>
                    <textarea
                      className="form-control"
                      name="footer_text_ar"
                      value={formData.footer_text_ar}
                      onChange={handleChange}
                      rows="2"
                      placeholder="أدخل نص الفوتر باللغة العربية"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>نص الفوتر (الإنجليزية)</label>
                    <textarea
                      className="form-control"
                      name="footer_text_en"
                      value={formData.footer_text_en}
                      onChange={handleChange}
                      rows="2"
                      placeholder="أدخل نص الفوتر باللغة الإنجليزية"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>نص السلايدر (العربية)</label>
                    <textarea
                      className="form-control"
                      name="slider_text_ar"
                      value={formData.slider_text_ar}
                      onChange={handleChange}
                      rows="2"
                      placeholder="أدخل نص السلايدر باللغة العربية"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>نص السلايدر (الإنجليزية)</label>
                    <textarea
                      className="form-control"
                      name="slider_text_en"
                      value={formData.slider_text_en}
                      onChange={handleChange}
                      rows="2"
                      placeholder="أدخل نص السلايدر باللغة الإنجليزية"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>عنوان السلايدر (العربية)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="slider_title_ar"
                      value={formData.slider_title_ar}
                      onChange={handleChange}
                      placeholder="أدخل عنوان السلايدر باللغة العربية"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>عنوان السلايدر (الإنجليزية)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="slider_title_en"
                      value={formData.slider_title_en}
                      onChange={handleChange}
                      placeholder="أدخل عنوان السلايدر باللغة الإنجليزية"
                    />
                  </div>
                </div>
              </div>

              {/* BUG-53 fix: فصل حقل شحن المنتج الثاني في قسم مستقل وواضح بأسفل الصفحة (كان مختلطاً داخل قسم "نصوص إضافية" غير المرتبط) */}
              <div className="mb-4">
                <h6 className={`mb-3 ${getAlignmentClass()}`}>إعدادات الشحن</h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className={`form-label ${getAlignmentClass()}`}>سعر المنتج الثاني في الشحن</label>
                    <input
                      type="number"
                      className="form-control"
                      required
                      min="0"
                      name="shipping_extra_per_item"
                      value={formData.shipping_extra_per_item}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* أزرار الحفظ والإلغاء */}
              <div className="text-center">
                <button
                  type="submit"
                  className="btn btn-success  btn-lg px-5 mx-3"
                  disabled={updateLoading || !hasChanges}
                >
                  {updateLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm mx-2" />
                      جار الحفظ...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save mx-2"></i>
                      حفظ التغييرات
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-lg px-5"
                  onClick={handleCancelEdit}
                >
                  <i className="bi bi-x-circle mx-2"></i>
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
      
    </div>
  );
}
